
import { AjinConfig } from '@ajin/config/ajin.config';
import { Features } from '@ajin/constants/features';
import { ScreenBreakpoints } from '@ajin/constants/screen-breakpoints';
import { Credential } from '@ajin/credentials/credential';
import { CredentialService } from '@ajin/credentials/credential.service';
import { IdCodeName } from '@ajin/entities/id-code-name';
import { MediaScreen } from '@ajin/entities/media-screen';
import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { inject, Injectable, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { SettingApiService } from '@projects/core/modules/settings2/services/setting-api.service';
import { Observable, of, Subject } from 'rxjs';


@Injectable({
	providedIn: 'root'
})
export class AppService {
	private _settingApiService = inject(SettingApiService);

	company: IdCodeName | null = null;

	id: string | null = null;
	code: number | null = null;
	name: string | null = null;
	key: string | null = null;
	icon: string | null = null;

	credential: Credential;
	clear: Subject<any> = new Subject<any>();
	screen: MediaScreen = new MediaScreen();
	event: Subject<any> = new Subject<any>();
	menuActions: MenuAction[] = [];
	config: AjinConfig;
	openSettings: Subject<void> = new Subject<void>();
	isNative = Capacitor.isNativePlatform();
	footerStatus: 'disabled' | 'active' = 'active';
	footerType: 'menu' | 'cancel-confirm' | 'disabled' = 'menu';
	headerStatus: string | null = 'active';
	logoStatus: string | null = 'active';

	timeZone: string | null = null;
	dateFormat: string | null = null;
	datetimeFormat: string | null = null;
	currencyFormat: string | null = null;
	percentageFormat: string | null = null;
	isSmall: boolean = false;

	platform: 'disabled' | 'active' = 'active';
	isBrowser: boolean;
	/** Whether the current browser is Microsoft Edge. */
	EDGE: boolean;
	/** Whether the current rendering engine is Microsoft Trident. */
	TRIDENT: boolean;
	/** Whether the current rendering engine is Blink. */
	BLINK: boolean;
	/** Whether the current rendering engine is WebKit. */
	WEBKIT: boolean;
	/** Whether the current platform is Apple iOS. */
	IOS: boolean;
	/** Whether the current browser is Firefox. */
	FIREFOX: boolean;
	/** Whether the current platform is Android. */
	ANDROID: boolean;
	/** Whether the current browser is Safari. */
	SAFARI: boolean;

	displayLoanValue = signal(true);
	displayLoanRate = signal(true);

	displayLoanSimulationValue = signal(true);
	displayLoanSimulationRate = signal(true);

	constructor(
		private breakpointObserver: BreakpointObserver,
		private credentialService: CredentialService,
		private mediaMatcher: MediaMatcher,
		private _title: Title,
		private router: Router
	) {

		this.breakpointObserver.observe([
			ScreenBreakpoints.sm,
			ScreenBreakpoints.md,
			ScreenBreakpoints.lg,
			ScreenBreakpoints.xl,
		]).subscribe({
			next: state => {
				this.screen.xs = !state.breakpoints[ScreenBreakpoints.sm];
				this.screen.sm = state.breakpoints[ScreenBreakpoints.sm];
				this.screen.md = state.breakpoints[ScreenBreakpoints.md];
				this.screen.lg = state.breakpoints[ScreenBreakpoints.lg];
				this.screen.xl = state.breakpoints[ScreenBreakpoints.xl];
				this.screen.change.next();

				if (!this.screen.sm) {
					this.isSmall = true;
				}
			}
		});

		this.logoStatus = localStorage.getItem('logo_status')?.toString() ?? 'active';
		this.headerStatus = localStorage.getItem('header_status')?.toString() ?? 'active';
	}

	setTitle(title: string) {
		this._title.setTitle(title);
	}

	setCredential(credential: Credential) {
		this.credential = credential;
		this.timeZone = this.credential?.timeZone;
	}

	loadCredential(): Observable<any> {
		return new Observable(observer => {
			this.credentialService.load().subscribe({
				next: () => {
					this.credential = this.credentialService.credential;
					this.timeZone = this.credential?.timeZone;
					this.loadDisplaySettings().subscribe();
					observer.next(this.credential);
					observer.complete();
				},
				error: error => {
					observer.error(error);
					observer.complete();
				}
			});
		});
	}

	setConfig(config: AjinConfig): Observable<any> {
		this.config = config;
		this.id = config.id;
		this.code = config.code;
		this.name = config.name;
		this.key = config.key;
		this.icon = config.icon;
		this.timeZone = config.timezone;
		this.dateFormat = config?.date?.format ?? 'dd/MM/yyyy';
		this.datetimeFormat = config?.datetime?.format ?? 'dd/MM/yyyy HH:mm';
		this.currencyFormat = config?.currency?.format ?? 'BRL';
		this.percentageFormat = config?.percentage?.format;
		return of(config);
	}

	activeFooter() {
		this.footerStatus = 'active';
	}

	disableFooter() {
		this.footerStatus = 'disabled';
	}

	emit(event: AppEventTypes): void {
		this.event?.next(event);
	}

	emitAny(event: any): void {
		this.event?.next(event);
	}

	toggle(): void {
		this.event?.next('toggle');
	}

	getRootUrl(): string {
		const url = new URL(window.location.href);
		return url.origin;
	}

	setMenuAction(event: MenuAction): void {
		this.menuActions ??= [];
		const exists = this.menuActions.some(item => item.key === event.key);
		if (!exists)
			this.menuActions.push(event);
	}

	removeMenuAction(key: string): void {
		this.menuActions ??= [];
		this.menuActions = this.menuActions.filter(item => item.key !== key);
	}

	loadDisplaySettings(): Observable<any> {
		return new Observable(observer => {
			this._settingApiService.getDisplaySettings().subscribe({
				next: (result: any) => {
					if (result) {
						let value1 = result.find(x => x.key == 'display_loan_value' && x.feature.code == Features.loan)?.value;
						let value2 = result.find(x => x.key == 'display_loan_rate' && x.feature.code == Features.loan)?.value;

						this.displayLoanValue.set(value1 === 'true');
						this.displayLoanRate.set(value2 === 'true');

						let value3 = result.find(x => x.key == 'display_loan_value' && x.feature.code == Features.loanSimulation)?.value;
						let value4 = result.find(x => x.key == 'display_loan_rate' && x.feature.code == Features.loanSimulation)?.value;

						this.displayLoanSimulationValue.set(value3 === 'true');
						this.displayLoanSimulationRate.set(value4 === 'true');
					}

					observer.next(result);
					observer.complete();
				},
				error: (error: any) => {
					observer.error(error);
					observer.complete();
				}
			});
		});
	}
}

export interface MenuAction {
	key?: string,
	title?: string,
	event?: string;
	icon?: string;
	position?: MenuActionPositions,
	tooltip?: string,
	style?: MenuActionStyles,
	color?: string
}

export type AppEventTypes = 'new' | 'closeLeftDrawer';
export type MenuActionPositions = 'header-right' | 'footer-left-1' | 'footer-left-2' | 'footer-center' | 'footer-right-1' | 'footer-right-2';
export type MenuActionStyles = 'mat-icon-button' | 'mat-icon-button';
